<template>
  <MainNavbar />
  <ToastContainer :toasts="toasts" @remove="removeToast" />
  <section class="facility-booking-page">
    
    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="error-content">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3 class="error-title">Failed to load facility</h3>
        <p class="error-message">{{ error }}</p>
        <button @click="fetchFacilityDetails" class="retry-btn">
          <i class="fas fa-redo"></i>
          Try Again
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="booking-container">
      <!-- Left Column - Facility Details -->
      <div class="facility-details-section">
        <!-- Image Gallery -->
        <div class="image-gallery">
          <div class="main-image-container">
            <div class="main-image">
              <img
                :src="currentImage"
                :alt="facility.name"
                class="main-image-img"
                decoding="async"
                @error="handleImageError"
              />
              <div class="image-overlay"></div>
              <div class="image-badges">
                <span class="price-badge">
                  <i class="fas fa-clock"></i>
                  Ksh {{ formatPrice(facility.hourly_rate) }} / hour
                </span>
                <span class="availability-badge" :class="{ available: facility.is_available }">
                  <i class="fas fa-circle"></i>
                  {{ facility.is_available ? 'Available' : 'Booked' }}
                </span>
              </div>
            </div>
            
            <div v-if="facility.images && facility.images.length > 1" class="image-navigation">
              <button 
                @click="prevImage" 
                class="nav-btn prev-btn"
                :disabled="currentImageIndex === 0"
                aria-label="Previous image"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button 
                @click="nextImage" 
                class="nav-btn next-btn"
                :disabled="currentImageIndex === facility.images.length - 1"
                aria-label="Next image"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
              
              <div class="image-counter">
                <i class="fas fa-image"></i>
                <span>{{ currentImageIndex + 1 }} / {{ facility.images.length }}</span>
              </div>
            </div>
          </div>
          
          <!-- Thumbnails -->
          <div v-if="facility.images && facility.images.length > 1" class="thumbnail-container">
            <div class="thumbnail-scroll">
              <div 
                v-for="(image, index) in facility.images" 
                :key="index"
                class="thumbnail-item"
                :class="{ active: currentImageIndex === index }"
                @click="currentImageIndex = index"
              >
                <img
                  :src="image"
                  :alt="`${facility.name} - Image ${index + 1}`"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Facility information -->
        <div class="facility-info-section">
          <!-- Header with Share Button Only -->
          <div class="facility-header">
            <div class="header-left">
              <span v-if="facility.type" class="sport-type-badge">
                <i :class="facilityTypeIcon(facility.type)" aria-hidden="true"></i>
                {{ formatFacilityType(facility.type) }}
              </span>
              <h1 class="facility-title">{{ facility.name || 'Facility' }}</h1>
              <div class="facility-location">
                <i class="fas fa-map-marker-alt"></i>
                <span class="location-text">
                  {{ facility.address }}, {{ facility.zone }}, {{ facility.resident_county }}
                </span>
              </div>
            </div>
            <div class="header-right">
              <button class="share-btn" @click="shareFacility" title="Share facility">
                <i class="fas fa-share-alt"></i>
              </button>
            </div>
          </div>

          <!-- Facility meta -->
          <div class="facility-meta-grid">
            <div class="meta-item" v-tooltip="'Sport / facility type'">
              <i :class="facilityTypeIcon(facility.type)"></i>
              <span>{{ formatFacilityType(facility.type) }}</span>
            </div>
            <div class="meta-item" v-tooltip="'Facility tier'">
              <i class="fas fa-layer-group"></i>
              <span>{{ facility.tier }}</span>
            </div>
            <div class="meta-item" v-tooltip="'Capacity'">
              <i class="fas fa-users"></i>
              <span>{{ facilityCapacityDisplay != null ? facilityCapacityDisplay + ' people' : 'Not specified' }}</span>
            </div>
          </div>

          <!-- Verified Badge -->
          <div v-if="facility.is_verified" class="verified-banner">
            <i class="fas fa-shield-alt"></i>
            <span>Verified Facility • Quality Assured</span>
          </div>

          <!-- Amenities Section -->
          <div v-if="facility.amenities && facility.amenities.length > 0" class="amenities-section">
            <h3 class="section-title">
              <i class="fas fa-check-circle"></i>
              Facility Amenities
            </h3>
            <div class="amenities-grid">
              <div 
                v-for="amenity in facility.amenities" 
                :key="amenity"
                class="amenity-card"
                v-tooltip="formatAmenity(amenity)"
              >
                <div class="amenity-icon">
                  <i :class="getAmenityIcon(amenity)"></i>
                </div>
                <span class="amenity-name">{{ formatAmenity(amenity) }}</span>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="description-section">
            <h3 class="section-title">
              <i class="fas fa-info-circle"></i>
              Facility Description
            </h3>
            <div class="description-content">
              <p v-if="facility.description">
                {{ facility.description }}
              </p>
              <p v-else>
                {{ facilityDescriptionFallback() }}
              </p>
              <div class="description-features">
                <div class="feature" v-if="facility.is_available">
                  <i class="fas fa-check"></i>
                  <span>Currently available</span>
                </div>
                <div class="feature" v-if="facilityCapacityDisplay != null">
                  <i class="fas fa-check"></i>
                  <span>Capacity: {{ facilityCapacityDisplay }} people</span>
                </div>
                <div class="feature" v-if="facility.is_verified">
                  <i class="fas fa-check"></i>
                  <span>Verified owner</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Owner Information -->
          <div class="owner-section">
            <h3 class="section-title">
              <i class="fas fa-user-circle"></i>
              Owner Information
            </h3>
            <div class="owner-card">
              <div class="owner-avatar">
                <img
                  :src="`https://ui-avatars.com/api/?name=${encodeURIComponent(facility.full_name || 'Facility Owner')}&background=random`"
                  :alt="facility.full_name || 'Facility Owner'"
                  loading="lazy"
                  decoding="async"
                />
                <div v-if="facility.is_verified" class="verified-dot">
                  <i class="fas fa-check"></i>
                </div>
              </div>
              <div class="owner-info">
                <h4 class="owner-name">{{ facility.full_name || 'Facility Owner' }}</h4>
                <div class="owner-contact">
                  <div class="contact-item">
                    <i class="fas fa-envelope"></i>
                    <span>{{ facility.email }}</span>
                  </div>
                  <div class="contact-item" v-if="facility.phone">
                    <i class="fas fa-phone"></i>
                    <span>{{ facility.phone }}</span>
                  </div>
                </div>
                <div class="owner-stats">
                  <span class="stat">
                    <i class="fas fa-calendar"></i>
                    Member since {{ formatDateShort(facility.ownership_start_date) }}
                  </span>
                  <span class="stat" v-if="facility.is_verified">
                    <i class="fas fa-check-circle"></i>
                    Verified
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- REMOVED: Reviews Preview Section -->
        </div>
      </div>

      <!-- Right Column - Booking Form -->
      <div class="booking-form-section">
        <div class="booking-card">
          <div class="booking-card-header">
            <h2 class="booking-title">
              <i class="fas fa-calendar-check"></i>
              Book This Facility
            </h2>
            <div class="booking-badge" :class="{ available: facility.is_available }">
              {{ facility.is_available ? 'Available Now' : 'Currently Booked' }}
            </div>
          </div>

          <div class="booking-form">
            <!-- Date Selection -->
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-calendar-day"></i>
                Select Date
              </label>
              <div class="date-input-wrapper">
                <input 
                  type="date" 
                  v-model="bookingForm.date"
                  required
                  :min="today"
                  class="date-input"
                />
                <i class="fas fa-calendar-alt date-icon"></i>
              </div>
            </div>

            <!-- Time Selection -->
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-clock"></i>
                Select Time Slot
              </label>
              <div class="time-selection-grid">
                <div class="time-input-group">
                  <label class="time-label">Start Time</label>
                  <select v-model="bookingForm.startTime" required class="time-select">
                    <option value="">Select start</option>
                    <option 
                      v-for="time in availableTimes" 
                      :key="time.value"
                      :value="time.value"
                    >
                      {{ time.label }}
                    </option>
                  </select>
                </div>
                <div class="time-separator">
                  <i class="fas fa-arrow-right"></i>
                </div>
                <div class="time-input-group">
                  <label class="time-label">End Time</label>
                  <select v-model="bookingForm.endTime" required class="time-select">
                    <option value="">Select end</option>
                    <option 
                      v-for="time in availableTimes" 
                      :key="time.value"
                      :value="time.value"
                      :disabled="isTimeBeforeStart(time.value)"
                    >
                      {{ time.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Duration Display -->
            <p v-if="bookingTimeInvalid" class="form-validation-error" role="alert">
              {{ bookingTimeInvalid }}
            </p>

            <div v-if="bookingForm.startTime && bookingForm.endTime" class="duration-display">
              <div class="duration-item">
                <span>Duration:</span>
                <span class="duration-value">{{ calculateDuration() }} hours</span>
              </div>
              <div class="duration-item">
                <span>Hourly Rate:</span>
                <span>Ksh {{ formatPrice(facility.hourly_rate) }}</span>
              </div>
            </div>

            <!-- Booking Summary -->
            <div v-if="calculateDuration() > 0" class="booking-summary">
              <h3 class="summary-title">
                <i class="fas fa-receipt"></i>
                Booking Summary
              </h3>
              <div class="summary-content">
                <div class="summary-row">
                  <span>{{ facility.name }}</span>
                  <span>Ksh {{ formatPrice(facility.hourly_rate) }}/hr</span>
                </div>
                <div class="summary-row">
                  <span>{{ calculateDuration() }} hours × Ksh {{ formatPrice(facility.hourly_rate) }}</span>
                  <span>Ksh {{ formatPrice(facility.hourly_rate * calculateDuration()) }}</span>
                </div>
                <div class="summary-divider"></div>
                <div class="summary-total">
                  <span>Total Amount</span>
                  <span class="total-amount">Ksh {{ calculateTotalCost() }}</span>
                </div>
              </div>
            </div>

            <!-- Additional Notes -->
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-sticky-note"></i>
                Additional Notes (Optional)
              </label>
              <textarea 
                v-model="bookingForm.notes"
                placeholder="Any special requirements, equipment needs, or notes..."
                rows="3"
                class="notes-textarea"
              ></textarea>
            </div>

            <!-- Submit Button -->
            <button 
              @click="showConfirmation"
              class="submit-booking-btn"
              :disabled="!isFormValid"
              :class="{ disabled: !isFormValid }"
            >
              <i class="fas fa-lock"></i>
              Book Now - Ksh {{ formatPrice(calculateTotalCost()) }}
            </button>

            <!-- Removed booking security section -->
          </div>
        </div>

        <!-- Quick Info Cards -->
        <div class="info-cards">
          <div class="info-card">
            <div class="info-icon">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <div class="info-content">
              <h4>Location Details</h4>
              <p>{{ facility.zone }}, {{ facility.resident_county }}</p>
              <button class="view-map-btn" @click="openMap">
                <i class="fas fa-directions"></i>
                Get Directions
              </button>
            </div>
          </div>

          <div class="info-card">
            <div class="info-icon">
              <i class="fas fa-question-circle"></i>
            </div>
            <div class="info-content">
              <h4>Need Help?</h4>
              <p>Contact our support team for assistance</p>
              <button class="contact-support-btn" @click="contactSupport">
                <i class="fas fa-headset"></i>
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">
              <i class="fas fa-check-circle"></i>
              Confirm Your Booking
            </h2>
            <button @click="closeModal" class="modal-close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="modal-body">
            <div class="booking-details">
              <div class="detail-section">
                <h3 class="detail-title">
                  <i class="fas fa-futbol"></i>
                  Facility Details
                </h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Facility Name:</span>
                    <span class="detail-value">{{ facility.name }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Location:</span>
                    <span class="detail-value">{{ facility.address }}, {{ facility.zone }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Type:</span>
                    <span class="detail-value">{{ formatFacilityType(facility.type) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Tier:</span>
                    <span class="detail-value">{{ facility.tier }}</span>
                  </div>
                </div>
              </div>

              <div class="detail-section">
                <h3 class="detail-title">
                  <i class="fas fa-calendar-alt"></i>
                  Booking Details
                </h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Date:</span>
                    <span class="detail-value">{{ formatDate(bookingForm.date) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Time Slot:</span>
                    <span class="detail-value">{{ bookingForm.startTime }} - {{ bookingForm.endTime }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Duration:</span>
                    <span class="detail-value">{{ calculateDuration() }} hours</span>
                  </div>
                </div>
              </div>

              <div class="detail-section">
                <h3 class="detail-title">
                  <i class="fas fa-receipt"></i>
                  Payment Details
                </h3>
                <div class="payment-breakdown">
                  <div class="payment-item">
                    <span>Hourly Rate:</span>
                    <span>Ksh {{ formatPrice(facility.hourly_rate) }}</span>
                  </div>
                  <div class="payment-item">
                    <span>Duration:</span>
                    <span>{{ calculateDuration() }} hours</span>
                  </div>
                  <div class="payment-item subtotal">
                    <span>Subtotal:</span>
                    <span>Ksh {{ formatPrice(facility.hourly_rate * calculateDuration()) }}</span>
                  </div>
                  <div class="payment-divider"></div>
                  <div class="payment-item total">
                    <span>Total Amount:</span>
                    <span class="total-amount">Ksh {{ formatPrice(calculateTotalCost()) }}</span>
                  </div>
                </div>
              </div>

              <div v-if="bookingForm.notes" class="detail-section">
                <h3 class="detail-title">
                  <i class="fas fa-sticky-note"></i>
                  Additional Notes
                </h3>
                <div class="notes-box">
                  <p>{{ bookingForm.notes }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button @click="closeModal" class="btn-edit">
              <i class="fas fa-edit"></i>
              Edit Details
            </button>
            <button @click="submitBooking" class="btn-confirm" :disabled="submitting">
              <i class="fas fa-check-circle"></i>
              {{ submitting ? 'Processing...' : 'Confirm & Pay Now' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
    <div class="edit-facility-footer-host">
      <ThirdpartyFooter />
    </div>
  </section>
</template>

<script>
/**
 * @fileoverview IpromiseAfrica customer booking page for a sports facility.
 * @module routes/facility/FacilityBookingForm
 *
 * - Loads facility: `GET /api/pitch-owners/pitches/:id` (`buildApiUrl`).
 * - Creates booking using sport-specific endpoint:
 *   - basketball: `POST /api/bookings/basketball/`
 *   - rugby/tennis/volleyball: `POST /api/bookings/<sport>/`
 *   - football (default): `POST /api/bookings/`
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";
import {
  formatFacilityType,
  facilityTypeIcon,
  facilityTypeNoun,
} from "@/utils/facilityTypes";

const MAX_BOOKING_HOURS = 4;

export default {
  name: "FacilityBooking",
  loadingMessage: 'Loading facility details...',
  mixins: [toastMixin, globalLoadingMixin],
  components: { MainNavbar, ThirdpartyFooter, ToastContainer },
  data() {
    return {
      facility: {
        images: [],
        amenities: []
      },
      currentImageIndex: 0,
      bookingForm: {
        date: '',
        startTime: '',
        endTime: '',
        notes: ''
      },
      today: new Date().toISOString().split('T')[0],
      loading: true,
      error: null,
      showModal: false,
      submitting: false,
    };
  },
  computed: {
    currentImage() {
      if (this.facility.images && this.facility.images.length > 0) {
        return this.facility.images[this.currentImageIndex];
      }
      return 'https://images.unsplash.com/photo-1529892485617-25f63cd7b1e9?w=600&h=400&fit=crop';
    },
    availableTimes() {
      const times = [];
      for (let hour = 6; hour <= 22; hour++) {
        const displayHour = hour % 12 || 12;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        times.push({
          value: `${hour.toString().padStart(2, '0')}:00`,
          label: `${displayHour}:00 ${ampm}`
        });
      }
      return times;
    },
    isFormValid() {
      if (!this.bookingForm.date || !this.bookingForm.startTime || !this.bookingForm.endTime) {
        return false;
      }
      if (this.bookingForm.startTime >= this.bookingForm.endTime) return false;
      const hours = this.durationHoursDecimal();
      if (hours <= 0 || hours > MAX_BOOKING_HOURS) return false;
      if (this.facility && this.facility.is_available === false) return false;
      return true;
    },
    bookingTimeInvalid() {
      if (!this.bookingForm.startTime || !this.bookingForm.endTime) return "";
      if (this.bookingForm.startTime >= this.bookingForm.endTime) {
        return "End time must be after start time.";
      }
      const h = this.durationHoursDecimal();
      if (h <= 0) return "End time must be after start time.";
      if (h > MAX_BOOKING_HOURS) {
        return `Maximum booking duration is ${MAX_BOOKING_HOURS} hours.`;
      }
      if (this.facility && this.facility.is_available === false) {
        return "This facility is not available for booking right now.";
      }
      return "";
    },
    /** Capacity from API (`capacity` or legacy `pitch_capacity`). */
    facilityCapacityDisplay() {
      const c = this.facility.capacity;
      return c != null && c !== "" ? c : null;
    },
  },
  mounted() {
    this.fetchFacilityDetails();
    this.initializeBookingForm();
  },
  watch: {
    "$route.params.id"(next, prev) {
      if (next && next !== prev) {
        this.currentImageIndex = 0;
        this.error = null;
        this.fetchFacilityDetails();
      }
    },
  },
  methods: {
    bookingCreateEndpoint() {
      const sport = String(this.facility?.type || "").trim().toLowerCase();
      if (sport.includes("basketball")) return "/api/bookings/basketball/";
      if (sport.includes("rugby")) return "/api/bookings/rugby/";
      if (sport.includes("tennis")) return "/api/bookings/tennis/";
      if (sport.includes("volleyball")) return "/api/bookings/volleyball/";
      return "/api/bookings/";
    },

    mapFacilityFromApi(data) {
      return {
        images: [],
        amenities: [],
        ...data,
        name: data.pitch_name,
        type: data.pitch_type,
        tier: data.pitch_tier,
        description: data.pitch_description,
        capacity: data.pitch_capacity ?? data.capacity,
        facilityId: data.pitch_id ?? data.id,
        phone: data.phone ?? data.owner_phone ?? data.phone_number ?? null,
        ownership_start_date: data.ownership_start_date ?? null,
      };
    },

    async fetchFacilityDetails() {
      this.loading = true;
      this.error = null;
      
      const facilityId = this.$route.params.id;
      try {
        const response = await fetch(buildApiUrl(`/api/facilities/${facilityId}`));
        
        if (!response.ok) {
          const errMsg = await this.parseBookingError(response);
          throw new Error(errMsg || `Failed to fetch facility (${response.status})`);
        }
        
        const data = await response.json();

        // Align with GET /api/pitch-owners/pitches/{id}: id, owner_phone, phone_number, optional `phone` alias
        this.facility = this.mapFacilityFromApi(data);
      } catch (error) {
        console.error("Error fetching facility:", error);
        this.error = error.message;
        this.showToast('error', 'Loading failed', error.message);
      } finally {
        this.loading = false;
      }
    },

    initializeBookingForm() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.bookingForm.date = tomorrow.toISOString().split('T')[0];
      
      this.bookingForm.startTime = '09:00';
      this.bookingForm.endTime = '10:00';
    },

    showConfirmation() {
      if (!this.isFormValid) return;
      if (!localStorage.getItem("access_token")) {
        this.showToast(
          "error",
          "Sign in required",
          "Please log in as a customer to book this facility on IpromiseAfrica."
        );
        return;
      }
      if (this.facility && this.facility.is_available === false) {
        this.showToast(
          "error",
          "Facility unavailable",
          "This facility is not available for booking right now."
        );
        return;
      }
      this.showModal = true;
    },

    /**
     * Duration in hours (decimal), aligned with server booking duration logic.
     */
    durationHoursDecimal() {
      if (!this.bookingForm.startTime || !this.bookingForm.endTime) return 0;
      const [sh, sm = "0"] = this.bookingForm.startTime.split(":");
      const [eh, em = "0"] = this.bookingForm.endTime.split(":");
      const startM = parseInt(sh, 10) * 60 + parseInt(String(sm).replace(/\D/g, "") || "0", 10);
      const endM = parseInt(eh, 10) * 60 + parseInt(String(em).replace(/\D/g, "") || "0", 10);
      return Math.max(0, (endM - startM) / 60);
    },

    /**
     * Parse FastAPI / HTTP error bodies into a single user-facing string.
     */
    async parseBookingError(response) {
      const text = await response.text();
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        return text || response.statusText || `Request failed (${response.status})`;
      }

      if (response.status === 401) {
        return (
          (body && body.detail && typeof body.detail === "string" && body.detail) ||
          "Your session expired or you are not signed in. Please log in and try again."
        );
      }

      const detail = body && body.detail;

      if (detail == null || detail === "") {
        return (
          (body && body.message) ||
          text ||
          response.statusText ||
          `Request failed (${response.status})`
        );
      }

      if (typeof detail === "string") {
        return detail;
      }

      if (Array.isArray(detail)) {
        return detail
          .map((d) => (d && (d.msg || d.message)) || JSON.stringify(d))
          .filter(Boolean)
          .join(" ");
      }

      if (typeof detail === "object") {
        if (detail.error === "TimeSlotAlreadyBooked" || detail.conflicting_booking) {
          const c = detail.conflicting_booking || {};
          const range =
            c.start_time && c.end_time
              ? `${c.start_time}–${c.end_time}`
              : "another booking";
          const st = c.status ? ` (${c.status})` : "";
          return (
            (detail.message || "This time slot overlaps an existing booking.") +
            ` Existing: ${range}${st}. ${detail.suggestion || "Please choose a different time."}`
          );
        }
        if (
          detail.error === "SelfBookingNotAllowed" ||
          detail.message === "You cannot book your own facility." ||
          detail.message === "You cannot book your own pitch."
        ) {
          return (
            detail.detail ||
            detail.message ||
            "You cannot book your own facility."
          );
        }
        if (detail.message) {
          return detail.detail
            ? `${detail.message} ${detail.detail}`
            : detail.message;
        }
        if (detail.msg) return detail.msg;
        try {
          return JSON.stringify(detail);
        } catch {
          return "Something went wrong. Please try again.";
        }
      }

      return String(detail);
    },

    closeModal() {
      this.showModal = false;
    },

    async submitBooking() {
      if (!this.isFormValid) return;

      const token = localStorage.getItem("access_token");
      if (!token) {
        this.showToast(
          "error",
          "Sign in required",
          "Please log in as a customer to complete your booking."
        );
        return;
      }

      this.submitting = true;

      const hours = this.durationHoursDecimal();
      const totalCost =
        Math.round(hours * (this.facility.hourly_rate || 0) * 100) / 100;

      const bookingData = {
        pitch_id: this.facility.facilityId,
        date: this.bookingForm.date,
        start_time: this.bookingForm.startTime,
        end_time: this.bookingForm.endTime,
        total_cost: totalCost,
        notes: this.bookingForm.notes || null
      };

      try {
        const response = await fetch(buildApiUrl(this.bookingCreateEndpoint()), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(bookingData)
        });

        if (response.ok) {
          let data = null;
          try {
            data = await response.json();
          } catch {
            data = null;
          }
          const ref =
            data && data.booking_number != null
              ? ` Booking #${data.booking_number}.`
              : "";
          this.showToast(
            "success",
            "Booking submitted",
            `Your request is pending confirmation.${ref} The facility owner will follow up by email.`
          );
          setTimeout(() => {
            this.$router.push("/ipromiseafrica/facility/available");
          }, 6000);
        } else {
          const message = await this.parseBookingError(response);
          throw new Error(message || `Booking failed (${response.status})`);
        }
      } catch (error) {
        console.error("Booking error:", error);
        const msg =
          error && error.message
            ? error.message
            : "Could not create the booking. Please try again.";
        this.showToast("error", "Booking failed", msg);
      } finally {
        this.submitting = false;
        this.showModal = false;
      }
    },

    nextImage() {
      if (this.facility.images && this.currentImageIndex < this.facility.images.length - 1) {
        this.currentImageIndex++;
      }
    },

    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },

    calculateDuration() {
      const h = this.durationHoursDecimal();
      return Math.round(h * 100) / 100;
    },

    calculateTotalCost() {
      return (
        Math.round(this.durationHoursDecimal() * (this.facility.hourly_rate || 0) * 100) /
        100
      );
    },

    isTimeBeforeStart(time) {
      if (!this.bookingForm.startTime) return false;
      return time <= this.bookingForm.startTime;
    },

    formatPrice(price) {
      if (!price) return '0';
      return new Intl.NumberFormat('en-KE').format(price);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },

    formatDateShort(dateString) {
      if (!dateString) return 'Recently';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short'
      });
    },

    handleImageError(event) {
      event.target.src = 'https://images.unsplash.com/photo-1529892485617-25f63cd7b1e9?w=600&h=400&fit=crop';
    },

    getAmenityIcon(amenity) {
      const key = String(amenity ?? "")
        .trim()
        .toLowerCase();
      const icons = {
        lighting: 'fas fa-lightbulb',
        changing: 'fas fa-restroom',
        changing_rooms: 'fas fa-restroom',
        parking: 'fas fa-parking',
        showers: 'fas fa-shower',
        equipment: 'fas fa-basketball-ball',
        cafe: 'fas fa-utensils',
        wifi: 'fas fa-wifi',
        first_aid: 'fas fa-first-aid',
        first_aid_kit: 'fas fa-first-aid',
        seating: 'fas fa-chair',
        security: 'fas fa-shield-alt',
        storage: 'fas fa-lock'
      };
      return icons[key] || 'fas fa-check-circle';
    },

    formatAmenity(amenity) {
      return String(amenity ?? "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },

    formatFacilityType,
    facilityTypeIcon,

    facilityDescriptionFallback() {
      const label = formatFacilityType(this.facility.type);
      const noun = facilityTypeNoun(this.facility.type);
      return `${label} ${noun} in ${this.facility.zone}, ${this.facility.resident_county}. ${this.facility.tier} tier with quality amenities on IpromiseAfrica.`;
    },


    shareFacility() {
      const url = window.location.href;
      const text = `Check out this IpromiseAfrica facility: ${this.facility.name}`;
      if (navigator.share) {
        navigator
          .share({
            title: this.facility.name,
            text,
            url,
          })
          .catch(() => {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () =>
            this.showToast(
              'success',
              'Link copied',
              'Facility link copied to clipboard.'
            ),
          () => this.showToast('info', 'Copy link', url)
        );
      } else {
        this.showToast('info', 'Share this page', url);
      }
    },

    openMap() {
      // Check if pin_location exists in the payload
      if (this.facility.pin_location) {
        // Directly open the pin_location URL from the payload
        window.open(this.facility.pin_location, '_blank');
      } else if (this.facility.address) {
        // Fallback to Google Maps search if pin_location is not available
        const address = encodeURIComponent(`${this.facility.address}, ${this.facility.zone}, ${this.facility.resident_county}`);
        window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
      } else {
        this.showToast('info', 'Location', 'Location information not available.');
      }
    },

    contactSupport() {
      this.showToast('info', 'Contact Support', 'Support contact feature coming soon!');
    },

  }
};
</script>

<style scoped>
/* Global Styles */
.facility-booking-page {
  min-height: 100vh;
  background: var(--background-color1);
  padding-top: 80px;
  position: relative;
}

.sport-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  background: rgba(0, 255, 255, 0.08);
  border: 1px solid rgba(0, 255, 255, 0.2);
}

[data-theme="dark"] .sport-type-badge {
  background: rgba(0, 151, 0, 0.08);
  border-color: rgba(0, 151, 0, 0.2);
}

.edit-facility-footer-host {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-shrink: 0;
}

.edit-facility-footer-host :deep(.thirdparty-footer) {
  margin-top: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px;
}

.spinner-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

.spinner {
  width: 100%;
  height: 100%;
  border: 4px solid rgba(var(--border-color-hover-rgb), 0.1);
  border-top-color: var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.spinner-ring {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 4px solid transparent;
  border-top-color: rgba(var(--border-color-hover-rgb), 0.3);
  border-radius: 50%;
  animation: spin 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite reverse;
}

.loading-text {
  color: var(--text-color2);
  font-size: 1.2rem;
  font-weight: 500;
  margin-top: 20px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

/* Error State */
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px;
}

.error-content {
  text-align: center;
  max-width: 400px;
  padding: 40px;
  background: var(--background-color2);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.error-icon {
  font-size: 3.5rem;
  color: #ff6b6b;
  margin-bottom: 20px;
}

.error-title {
  color: var(--text-color1);
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.error-message {
  color: var(--text-color2);
  margin-bottom: 25px;
}

.retry-btn {
  padding: 12px 30px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(var(--border-color-hover-rgb), 0.3);
}

/* Main Container */
.booking-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px 20px;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 40px;
  align-items: start;
}

/* Responsive Grid */
@media (max-width: 1200px) {
  .booking-container {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .booking-form-section {
    position: static;
  }
}

@media (max-width: 768px) {
  .booking-container {
    padding: 15px;
    gap: 20px;
  }
}

/* Image Gallery */
.image-gallery {
  margin-bottom: 40px;
}

.main-image-container {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 20px;
  background: var(--background-color2);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.main-image {
  position: relative;
  height: 400px;
  overflow: hidden;
}

.main-image-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-image:hover .main-image-img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.5));
}

.image-badges {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
}

.price-badge,
.availability-badge {
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

.price-badge {
  background: linear-gradient(135deg, rgba(var(--border-color-hover-rgb), 0.9), rgba(0, 255, 255, 0.7));
  color: white;
}

.availability-badge {
  background: rgba(0, 0, 0, 0.8);
  color: #ff6b6b;
}

.availability-badge.available {
  color: #4CAF50;
}

/* Responsive Image Badges */
@media (max-width: 768px) {
  .image-badges {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .main-image {
    height: 300px;
  }
}

@media (max-width: 480px) {
  .main-image {
    height: 250px;
  }
  
  .price-badge,
  .availability-badge {
    padding: 8px 16px;
    font-size: 0.85rem;
  }
}

.image-navigation {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-btn {
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

@media (max-width: 480px) {
  .nav-btn {
    width: 40px;
    height: 40px;
  }
}

.nav-btn:hover:not(:disabled) {
  background: rgba(var(--border-color-hover-rgb), 0.9);
  transform: scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.image-counter {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Thumbnail Container */
.thumbnail-container {
  overflow-x: auto;
  padding-bottom: 10px;
  -webkit-overflow-scrolling: touch;
}

.thumbnail-scroll {
  display: flex;
  gap: 10px;
  padding: 5px;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 80px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

@media (max-width: 480px) {
  .thumbnail-item {
    width: 70px;
    height: 50px;
  }
}

.thumbnail-item:hover {
  transform: translateY(-2px);
}

.thumbnail-item.active {
  border-color: var(--border-color-hover);
  box-shadow: 0 4px 15px rgba(var(--border-color-hover-rgb), 0.4);
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Facility info section */
.facility-info-section {
  background: var(--background-color2);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .facility-info-section {
    padding: 20px;
  }
}

.facility-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px solid var(--border-color-hover1);
}

@media (max-width: 768px) {
  .facility-header {
    flex-direction: column;
    gap: 15px;
  }
}

.header-left {
  flex: 1;
}

.facility-title {
  color: var(--text-color1);
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 10px;
  line-height: 1.2;
}

@media (max-width: 768px) {
  .facility-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .facility-title {
    font-size: 1.5rem;
  }
}

.facility-location {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color2);
  font-size: 1.1rem;
}

@media (max-width: 480px) {
  .facility-location {
    font-size: 1rem;
  }
}

.facility-location i {
  color: var(--border-color-hover);
}

.header-right {
  display: flex;
  gap: 10px;
}

@media (max-width: 768px) {
  .header-right {
    align-self: flex-end;
  }
}

.share-btn,
.save-btn {
  width: 45px;
  height: 45px;
  border: none;
  border-radius: 50%;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-btn:hover {
  background: var(--border-color-hover);
  color: var(--text-color3);
  transform: scale(1.1);
}

.save-btn:hover,
.save-btn.saved {
  background: #ff4444;
  color: white;
  transform: scale(1.1);
}

/* Facility meta grid */
.facility-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

@media (max-width: 768px) {
  .facility-meta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .facility-meta-grid {
    grid-template-columns: 1fr;
  }
}

.meta-item {
  background: var(--background-color1);
  padding: 15px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-color1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
}

.meta-item:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.meta-item i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
}

/* Verified Banner */
.verified-banner {
  background: linear-gradient(135deg, rgba(var(--border-color-hover-rgb), 0.1), rgba(var(--border-color-hover-rgb), 0.2));
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.3);
  border-radius: 12px;
  padding: 15px 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-color1);
}

.verified-banner i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
}

/* Section Common Styles */
.section-title {
  color: var(--text-color1);
  font-size: 1.3rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 480px) {
  .section-title {
    font-size: 1.1rem;
  }
}

.section-title i {
  color: var(--border-color-hover);
}

/* Amenities Section */
.amenities-section {
  margin-bottom: 30px;
}

.amenities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
}

@media (max-width: 768px) {
  .amenities-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .amenities-grid {
    grid-template-columns: 1fr;
  }
}

.amenity-card {
  background: var(--background-color1);
  padding: 20px 15px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
}

.amenity-card:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.amenity-icon {
  width: 40px;
  height: 40px;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-size: 1.2rem;
  color: var(--border-color-hover);
}

.amenity-name {
  color: var(--text-color2);
  font-size: 0.9rem;
  line-height: 1.3;
  display: block;
}

/* Description Section */
.description-section {
  margin-bottom: 30px;
}

.description-content {
  background: var(--background-color1);
  padding: 25px;
  border-radius: 15px;
  border: 1px solid var(--border-color-hover1);
}

@media (max-width: 768px) {
  .description-content {
    padding: 20px;
  }
}

.description-content p {
  color: var(--text-color2);
  line-height: 1.6;
  margin-bottom: 20px;
}

.description-features {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color1);
}

.feature i {
  color: var(--border-color-hover);
}

/* Owner Section */
.owner-section {
  margin-bottom: 30px;
}

.owner-card {
  background: var(--background-color1);
  padding: 25px;
  border-radius: 15px;
  display: flex;
  gap: 20px;
  align-items: center;
  border: 1px solid var(--border-color-hover1);
}

@media (max-width: 480px) {
  .owner-card {
    flex-direction: column;
    text-align: center;
    gap: 15px;
    padding: 20px;
  }
}

.owner-avatar {
  position: relative;
  flex-shrink: 0;
}

.owner-avatar img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--border-color-hover);
}

.verified-dot {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background: var(--border-color-hover);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  border: 2px solid var(--background-color1);
}

.owner-info {
  flex: 1;
}

.owner-name {
  color: var(--text-color1);
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.owner-contact {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
}

@media (max-width: 480px) {
  .owner-contact,
  .owner-stats {
    justify-content: center;
  }
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color2);
}

.contact-item i {
  color: var(--border-color-hover);
  width: 20px;
}

.owner-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.stat i {
  color: var(--primary-color);
}

/* Reviews Preview */
.reviews-preview {
  margin-bottom: 30px;
}

.reviews-container {
  background: var(--background-color1);
  padding: 25px;
  border-radius: 15px;
  border: 1px solid var(--border-color-hover1);
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .reviews-container {
    padding: 20px;
  }
}

.review-item {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border-color-hover1);
}

.review-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.reviewer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reviewer-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
}

.reviewer-name {
  color: var(--text-color1);
  margin-bottom: 5px;
  font-size: 1rem;
}

.review-rating {
  display: flex;
  gap: 2px;
}

.review-rating i {
  color: #ddd;
  font-size: 0.9rem;
}

.review-rating i.filled {
  color: var(--primary-color);
}

.review-date {
  color: var(--text-color2);
  font-size: 0.85rem;
}

.review-text {
  color: var(--text-color2);
  line-height: 1.5;
}

.view-all-reviews {
  width: 100%;
  padding: 12px;
  background: transparent;
  color: var(--border-color-hover);
  border: 2px solid var(--border-color-hover);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.view-all-reviews:hover {
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-2px);
}

/* Booking Form Section */
.booking-form-section {
  position: sticky;
  top: 100px;
}

@media (max-width: 1200px) {
  .booking-form-section {
    position: static;
  }
}

.booking-card {
  background: var(--background-color2);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .booking-card {
    padding: 20px;
  }
}

.booking-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px solid var(--border-color-hover1);
}

.booking-title {
  color: var(--text-color1);
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 480px) {
  .booking-title {
    font-size: 1.2rem;
  }
}

.booking-title i {
  color: var(--border-color-hover);
}

.booking-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #ff6b6b;
  color: white;
}

.booking-badge.available {
  background: #4CAF50;
}

/* Form Styles */
.booking-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  color: var(--text-color1);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
}

.form-validation-error {
  color: var(--notification-error-border, #e74c3c);
  font-size: 0.9rem;
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.25);
  line-height: 1.45;
}

.date-input-wrapper {
  position: relative;
}

.date-input {
  width: 100%;
  padding: 12px 45px 12px 15px;
  border: 2px solid var(--border-color-hover1);
  border-radius: 10px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.date-input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
}

.date-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  pointer-events: none;
}

.time-selection-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 15px;
  align-items: end;
}

@media (max-width: 768px) {
  .time-selection-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .time-separator {
    padding: 5px 0;
    transform: rotate(90deg);
    display: flex;
    justify-content: center;
  }
}

.time-input-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.time-label {
  color: var(--text-color1);
  font-size: 0.9rem;
  font-weight: 600;
}

.time-select {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid var(--border-color-hover1);
  border-radius: 10px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.time-select:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
}

.time-separator {
  padding-bottom: 8px;
  color: var(--text-color2);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Duration Display */
.duration-display {
  background: var(--background-color1);
  padding: 15px;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover1);
}

.duration-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color2);
  margin-bottom: 8px;
}

.duration-item:last-child {
  margin-bottom: 0;
}

.duration-value {
  color: var(--text-color1);
  font-weight: 600;
}

/* Booking Summary */
.booking-summary {
  background: var(--background-color1);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid var(--border-color-hover1);
}

.summary-title {
  color: var(--text-color1);
  margin-bottom: 20px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-title i {
  color: var(--border-color-hover);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color2);
  font-size: 0.95rem;
  flex-wrap: wrap;
  gap: 5px;
}

.summary-divider {
  height: 1px;
  background: var(--border-color-hover1);
  margin: 5px 0;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color1);
  font-weight: 600;
  font-size: 1.1rem;
  padding-top: 10px;
  flex-wrap: wrap;
  gap: 5px;
}

.total-amount {
  color: var(--border-color-hover);
  font-size: 1.3rem;
}

/* Notes Textarea */
.notes-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid var(--border-color-hover1);
  border-radius: 10px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 1rem;
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 100px;
}

.notes-textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
}

.notes-textarea::placeholder {
  color: var(--text-color2);
}

/* Submit Button */
.submit-booking-btn {
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, var(--border-color-hover), rgba(0, 255, 255, 0.8));
  color: var(--text-color3);
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  overflow: hidden;
}

@media (max-width: 480px) {
  .submit-booking-btn {
    padding: 15px;
    font-size: 1rem;
  }
}

.submit-booking-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.submit-booking-btn:hover:not(.disabled)::before {
  left: 100%;
}

.submit-booking-btn:hover:not(.disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(var(--border-color-hover-rgb), 0.4);
}

.submit-booking-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Info Cards */
.info-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  background: var(--background-color2);
  border-radius: 15px;
  padding: 25px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color-hover1);
}

@media (max-width: 480px) {
  .info-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;
  }
}

.info-icon {
  width: 50px;
  height: 50px;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: var(--border-color-hover);
  flex-shrink: 0;
}

.info-content {
  flex: 1;
}

.info-content h4 {
  color: var(--text-color1);
  margin-bottom: 8px;
  font-size: 1.1rem;
}

.info-content p {
  color: var(--text-color2);
  margin-bottom: 15px;
  font-size: 0.9rem;
  line-height: 1.4;
}

.view-map-btn,
.contact-support-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.view-map-btn {
  background: var(--background-color1);
  color: var(--border-color-hover);
  border: 2px solid var(--border-color-hover);
}

.view-map-btn:hover {
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-2px);
}

.contact-support-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.contact-support-btn:hover {
  background: rgba(var(--border-color-hover-rgb), 0.9);
  transform: translateY(-2px);
}

/* Modal Styles */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: var(--background-color2);
  border-radius: 25px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .modal-content {
    margin: 10px;
  }
}

.modal-header {
  padding: 30px;
  border-bottom: 2px solid var(--border-color-hover1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@media (max-width: 768px) {
  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 20px;
  }
}

.modal-title {
  color: var(--text-color1);
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title i {
  color: var(--border-color-hover);
}

.modal-close-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background: var(--border-color-hover);
  color: var(--text-color3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 30px;
}

.booking-details {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.detail-section {
  background: var(--background-color1);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid var(--border-color-hover1);
}

@media (max-width: 768px) {
  .detail-section {
    padding: 20px;
  }
}

.detail-title {
  color: var(--text-color1);
  margin-bottom: 20px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-title i {
  color: var(--border-color-hover);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.detail-label {
  color: var(--text-color2);
  font-size: 0.9rem;
}

.detail-value {
  color: var(--text-color1);
  font-weight: 600;
}

.payment-breakdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color2);
  flex-wrap: wrap;
  gap: 5px;
}

.payment-item.subtotal {
  color: var(--text-color1);
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid var(--border-color-hover1);
}

.payment-divider {
  height: 1px;
  background: var(--border-color-hover1);
  margin: 5px 0;
}

.payment-item.total {
  color: var(--text-color1);
  font-weight: 700;
  font-size: 1.2rem;
}

.total-amount {
  color: var(--border-color-hover);
  font-size: 1.4rem;
}

.notes-box {
  background: var(--background-color3);
  padding: 20px;
  border-radius: 10px;
  color: var(--text-color2);
  font-style: italic;
}

.modal-footer {
  padding: 25px 30px;
  border-top: 2px solid var(--border-color-hover1);
  display: flex;
  gap: 15px;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .modal-footer {
    flex-direction: column;
  }
}

.btn-edit,
.btn-confirm {
  padding: 14px 28px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 768px) {
  .btn-edit,
  .btn-confirm {
    width: 100%;
    justify-content: center;
  }
}

.btn-edit {
  background: transparent;
  color: var(--text-color2);
  border: 2px solid var(--border-color-hover1);
}

.btn-edit:hover {
  border-color: var(--text-color2);
  color: var(--text-color1);
}

.btn-confirm {
  background: linear-gradient(135deg, var(--border-color-hover), rgba(0, 255, 255, 0.8));
  color: var(--text-color3);
}

.btn-confirm:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(0, 255, 255, 0.9), var(--border-color-hover));
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(var(--border-color-hover-rgb), 0.4);
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Touch Device Optimizations */
@media (hover: none) and (pointer: coarse) {
  .meta-item:hover,
  .amenity-card:hover,
  .thumbnail-item:hover,
  .view-all-reviews:hover,
  .view-map-btn:hover,
  .contact-support-btn:hover,
  .submit-booking-btn:hover:not(.disabled),
  .btn-edit:hover,
  .btn-confirm:hover:not(:disabled) {
    transform: none;
  }
  
  .nav-btn,
  .share-btn,
  .save-btn,
  .submit-booking-btn,
  .btn-edit,
  .btn-confirm {
    min-height: 44px;
    min-width: 44px;
  }
  
  .date-input,
  .time-select,
  .notes-textarea {
    font-size: 16px; /* Prevents iOS zoom */
  }
}

/* Ensure proper spacing for mobile keyboards */
@media (max-height: 700px) and (orientation: landscape) {
  .booking-form-section {
    position: static;
    margin-bottom: 20px;
  }
}

/* Accessibility Improvements */
.nav-btn:focus,
.date-input:focus,
.time-select:focus,
.notes-textarea:focus,
.submit-booking-btn:focus,
.modal-close-btn:focus {
  outline: 2px solid var(--border-color-hover);
  outline-offset: 2px;
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
  
  .main-image:hover .main-image-img {
    transform: none;
  }
  
  .meta-item:hover,
  .amenity-card:hover,
  .nav-btn:hover:not(:disabled),
  .share-btn:hover,
  .save-btn:hover,
  .submit-booking-btn:hover:not(.disabled),
  .btn-edit:hover,
  .btn-confirm:hover:not(:disabled) {
    transform: none;
  }
}

/* Final responsive adjustments */
@media (max-width: 360px) {
  .facility-title {
    font-size: 1.3rem;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .booking-title {
    font-size: 1.2rem;
  }
  
  .meta-item,
  .amenity-card {
    padding: 12px;
  }
  
  .info-card {
    padding: 15px;
  }
  
  .booking-card {
    padding: 15px;
  }
}

/* Ensure images maintain aspect ratio */
.main-image-img,
.thumbnail-item img,
.reviewer-avatar,
.owner-avatar img {
  aspect-ratio: 16/9;
}

.thumbnail-item img {
  aspect-ratio: 4/3;
}

.reviewer-avatar,
.owner-avatar img {
  aspect-ratio: 1/1;
}
</style>